import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    TransportOrderListItemDto,
    TransportOrderState
} from "../../models/transport/transportOrder";
import { Box, Checkbox, FormControlLabel, IconButton, Popover, Typography } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useTranslation } from "react-i18next";
import { getOwnTransportOrders } from "../../services/transportOrderService";
import { useSelectCurrentWorkShiftTimeSlot } from "../../hooks/worktime/useSelectWorkShiftTimeSlots";
import { handleApiError } from "../../services/baseService";
import { fetchCurrentWorkShift } from "../../framework/redux/slices/workShiftSlice";
import { useAsyncThunkAction } from "../../hooks/framework/useAsyncThunkAction";
import { OrderCard } from "./transportOrder/orderCard";
import { Base } from "../../framework/common/base";
import { FilterList } from "@mui/icons-material";

export const TransportList = ({ title }: { title: string }) => {
    const { t } = useTranslation();
    const [transportOrders, setTransportOrders] = useState<TransportOrderListItemDto[]>([]);
    const [showDelivered, setShowDelivered] = useState(false);
    const [showCancelled, setShowCancelled] = useState(false);
    const [showUpcoming, setShowUpcoming] = useState(false);
    const [statesToFetch, setStatesToFetch] = useState([TransportOrderState.OrderCreated, TransportOrderState.PickedUp]);
    const currentTimeSlot = useSelectCurrentWorkShiftTimeSlot();

    const [endDate, setEndDate] = useState(Base.dateToDateJSON());
    const startDate = useMemo(
        () => Base.dateToDateJSON(Base.dateAddMonths(endDate, -3).startOf("month")),
        []
    );

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const id = open ? "order-filters" : undefined;

    useAsyncThunkAction(
        () => fetchCurrentWorkShift(),
        { onError: handleApiError },
        []
    );

    const fetchTransportOrders = useCallback(() => {
        getOwnTransportOrders({
            vehicleId: currentTimeSlot?.vehicleId || "",
            selectedOrderStates: statesToFetch,
            timeFrameStartDate: startDate,
            timeFrameEndDate: endDate,
        })
            .then((res) => setTransportOrders(res.data))
            .catch((e) => console.error(e));
    }, [currentTimeSlot?.vehicleId, statesToFetch, startDate, endDate]);

    useEffect(() => {
        fetchTransportOrders();
    }, [fetchTransportOrders]);

    useEffect(() => {
        const states = statesToFetch;
        if (showDelivered) {
            const newStateToShow = [TransportOrderState.Delivered];
            setStatesToFetch(states.concat(newStateToShow));
        } else {
            const newStates = states.filter(p => p !== TransportOrderState.Delivered);
            setStatesToFetch(newStates);
        }
    }, [showDelivered]);

    useEffect(() => {
        const states = statesToFetch;
        if (showCancelled) {
            const newStateToShow = [TransportOrderState.Cancelled];
            setStatesToFetch(states.concat(newStateToShow));
        } else {
            const newStates = states.filter(p => p !== TransportOrderState.Cancelled);
            setStatesToFetch(newStates);
        }
    }, [showCancelled]);

    useEffect(() => {
        if (showUpcoming) {
            // Show orders for next two weeks
            setEndDate(Base.dateToDateJSON(Base.dateAddDays(Base.dateToDateJSON(), 14).endOf("week")));
        } else {
            // Back to default time period
            setEndDate(Base.dateToDateJSON());
        }
    }, [showUpcoming]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Grid2 container justifyContent="space-between" alignItems="center" mx={2} mt={2}>
                <Typography variant="h4" sx={{ color: "primary.dark" }}>
                    {title}
                </Typography>
                <Box>
                    <IconButton
                        onClick={handleClick}
                        color="primary"
                        size="large"
                    >
                        <FilterList fontSize="inherit" />
                    </IconButton>
                    <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                        }}
                    >
                        <Box mx={2} my={1}>
                            <Box>
                                <Grid2 container justifyContent="start" alignItems="center">
                                    <FormControlLabel control={
                                        <Checkbox
                                            checked={showDelivered}
                                            onChange={() => {setShowDelivered((prev) => !prev);}}
                                        />}
                                        label={t("transport.order.showDeliveredOrders")}
                                    />
                                </Grid2>
                            </Box>
                            <Box>
                                <Grid2 container justifyContent="start" alignItems="center">
                                    <FormControlLabel control={
                                        <Checkbox
                                            checked={showCancelled}
                                            onChange={() => {setShowCancelled((prev) => !prev);}}
                                        />}
                                        label={t("transport.order.showCancelledOrders")}
                                    />
                                </Grid2>
                            </Box>
                            <Box>
                                <Grid2 container justifyContent="start" alignItems="center">
                                    <FormControlLabel control={
                                        <Checkbox
                                            checked={showUpcoming}
                                            onChange={() => {setShowUpcoming((prev) => !prev);}}
                                        />}
                                        label={t("transport.order.showUpcomingOrders")}
                                    />
                                </Grid2>
                            </Box>
                        </Box>
                    </Popover>
                </Box>
            </Grid2>

            {transportOrders.map((order) => (
                <OrderCard
                    key={order.id}
                    transportOrder={order}
                    fetchTransportOrders={fetchTransportOrders}
                    showOrderStateToggle
                />
            ))}
        </>
    );
};
